import { defineStore } from "pinia";
import { computed, ref } from "vue";
import * as authApi from "@/api/auth";
import { tokenStore, type ImpersonationMeta } from "@/api/client";
import type { User, UserSubscriptions } from "@/types";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Track impersonation in a reactive ref so the banner re-renders the
  // moment a tab starts/stops impersonating. Initialised from sessionStorage
  // so a refresh inside an impersonation tab preserves the state.
  const impersonation = ref<ImpersonationMeta | null>(tokenStore.getImpersonation());

  const isAuthenticated = computed(() => user.value !== null);
  const isImpersonating = computed(() => impersonation.value !== null);
  const isServerAdmin = computed(
    () => user.value?.permissions?.is_server_admin === true || user.value?.is_superuser === true,
  );
  const canManagePlants = computed(
    () => isServerAdmin.value || user.value?.permissions?.can_manage_plants === true,
  );
  const canManageControllers = computed(
    () => isServerAdmin.value || user.value?.permissions?.can_manage_controllers === true,
  );
  const canManageUsers = computed(
    () => isServerAdmin.value || user.value?.permissions?.can_manage_users === true,
  );
  const hasRwAccess = computed(
    () => isServerAdmin.value || user.value?.permissions?.rw_tags_access === true,
  );
  const canDesign = computed(
    () => isServerAdmin.value || user.value?.permissions?.can_design === true,
  );

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;
    try {
      const pair = await authApi.login({ email, password });
      user.value = pair.user;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      error.value = err.response?.data?.detail || "Login failed";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload: authApi.RegisterPayload) {
    loading.value = true;
    error.value = null;
    try {
      await authApi.register(payload);
      // Auto-login after successful registration
      await login(payload.email, payload.password);
    } finally {
      loading.value = false;
    }
  }

  async function fetchMe() {
    if (!tokenStore.getAccess()) return;
    try {
      user.value = await authApi.fetchCurrentUser();
    } catch {
      user.value = null;
      tokenStore.clear();
    }
  }

  /**
   * Osvježi korisnikovu početnu stranicu u memoriji nakon promjene na
   * dashboardu — ruter je čita odavde pri pokretanju, pa bez ovoga nova
   * postavka vrijedi tek nakon osvježavanja stranice.
   */
  function setLanding(pageId: number | null, mode?: string | null) {
    if (!user.value) return;
    user.value = {
      ...user.value,
      landing_page: pageId,
      landing_page_mode: pageId ? (mode ?? null) : null,
    };
  }

  async function updateMySubscriptions(payload: Partial<UserSubscriptions>) {
    const subs = await authApi.updateMySubscriptions(payload);
    if (user.value) user.value = { ...user.value, subscriptions: subs };
    return subs;
  }

  function logout() {
    authApi.logout();
    user.value = null;
    impersonation.value = null;
  }

  /**
   * Called by the impersonation handoff view after it stashes the new
   * tokens in sessionStorage. Picks up the meta so the banner can render.
   */
  function adoptImpersonation(meta: ImpersonationMeta) {
    impersonation.value = meta;
  }

  /**
   * Stop the current impersonation session: clear the per-tab tokens, drop
   * the in-memory user, and (by default) close the tab so the admin returns
   * to their original window. If window.close fails (browsers block it for
   * tabs they didn't open), fall back to redirecting to login in this tab.
   */
  function stopImpersonation(closeTab = true) {
    tokenStore.clear();
    user.value = null;
    impersonation.value = null;
    if (closeTab) {
      // Will succeed for tabs opened via window.open (which is how we
      // launch impersonation). Tabs opened by hand stay open.
      window.close();
    }
  }

  // Listen for token expiry from axios interceptor
  if (typeof window !== "undefined") {
    window.addEventListener("auth:expired", () => {
      user.value = null;
      // Impersonation tokens auto-expire after IMPERSONATE_TTL_MINUTES; when
      // that happens we need to drop the banner state too.
      impersonation.value = null;
    });
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    isServerAdmin,
    canManagePlants,
    canManageControllers,
    canManageUsers,
    hasRwAccess,
    canDesign,
    isImpersonating,
    impersonation,
    login,
    register,
    fetchMe,
    setLanding,
    updateMySubscriptions,
    logout,
    adoptImpersonation,
    stopImpersonation,
  };
});
